/* Every screen path, in one place — and in `shared/`, not `app/`.

 * `app/` declares the routes and a feature links to them, so both need to read this. FSD's stack is
 * app -> features -> entities -> shared, inward only, and ESLint enforces it: a constant in `app/`
 * would be unreachable from `DegradedBanner`, which would then hold its own copy of the path. Two
 * copies of a route is how the collision this file exists to prevent gets re-created.
 *
 *
 * Declared as data rather than only as JSX so a test can enumerate them. That is not tidiness: the
 * `/readiness` collision — a screen route shadowed by the operational probe on any hard navigation —
 * could not be caught by a test that had no way to ask "what paths does this app claim".
 *
 * `App.tsx` renders from these constants, so the list cannot describe a route that does not exist,
 * and `routes.test.ts` checks each one against `SERVER_PATH_PREFIXES`.
 */

export const SCREEN_PATHS = {
  /* The landing page, and the one route that is not a feature. `/` rather than `/home` because a
   * reader who clears the address bar expects the product's front door, and a redirect from `/` to
   * `/home` is a round trip that exists only to satisfy a naming preference. */
  home: '/',
  connections: '/connections',
  /** Nested under the list, the same way `dataset` is nested under `datasets` below — the
   * breadcrumb back to the source list is a real parent route, not a guess. */
  connection: '/connections/:connectionId',
  datasets: '/datasets',
  /** One source's catalogue: the tables a harvest of it discovered.
   *
   * **`/datasets/source/:id` rather than `/datasets/:connectionId`**, because a bare id under
   * `/datasets` already means a *dataset* — and two sibling routes whose parameter is only
   * distinguishable by which table it happens to exist in is the shape that produces a 404 nobody
   * can explain. The literal segment makes the two unambiguous to a reader as well as to a router.
   */
  datasetsBySource: '/datasets/source/:connectionId',
  /** Nested under the list so the breadcrumb back to the catalog is a real parent, not a guess. */
  dataset: '/datasets/:datasetId',
  review: '/review',
  rules: '/rules',
  /** One rule, reached from the list's `Open`. Nested under it so the breadcrumb back to the list is
   * a real parent — the same shape `dataset` and `connection` already have. */
  rule: '/rules/:ruleId',
  executions: '/executions',
  /* One run: its plan, its per-dataset tasks, and its violations.
   *
   * **A route rather than a panel under the list, and that is a bug fix as much as a layout
   * choice.** "View violations" expanded a card *below* every execution card on the screen, so on a
   * tenant with a few runs the thing a reader had just asked for rendered off the fold and the click
   * appeared to do nothing. A run is also the natural unit to link to and to send somebody — an
   * incident review asks "what did run X find", and that had no address. */
  execution: '/executions/:executionId',
  scores: '/scores',
  lineage: '/lineage',
  incidents: '/incidents',
  /* One incident's own page. Reached from the queue and bookmarkable, which is the point: an
   * incident review arrives at a *specific* incident from an alert or a ticket, and before this
   * the only address was the queue — where a resolved incident does not appear at all. */
  incident: '/incidents/:incidentId',
  /* `/alert-routing` rather than `/notifications`: the screen's subject is *where* an alert
     goes, and a reader looking for that asks about routing. `/notifications` would also invite
     an in-app notification centre onto the same path, which is a different thing. */
  alertRouting: '/alert-routing',
  remediation: '/remediation',
  /* One plan: what it would change, what it cannot, and its emitted artifact.
   *
   * Nested under the queue so the breadcrumb back to it is a real parent, the shape `rule`,
   * `dataset`, `anomaly` and `contract` already have. */
  remediationPlan: '/remediation/:planId',
  compliance: '/compliance',
  /* One framework's controls, parameterised on **both** halves of its identity.
   *
   * A framework is `(code, version)` and neither alone names one: a pack is generated against one
   * framework *at one version*, and the version is part of what it evidences. So two segments rather
   * than a `code@version` slug — a slug would need escaping and would read as one opaque id in a URL
   * an auditor may well be asked to quote.
   *
   * Under `/compliance/` rather than a top-level path, because it is reached by opening a row on the
   * compliance screen and the breadcrumb says so. */
  complianceFramework: '/compliance/:frameworkCode/:frameworkVersion',
  contracts: '/contracts',
  /* One contract: its terms, its two signatures and what the last evaluation found.
   *
   * Nested under the ledger so the breadcrumb back to it is a real parent, the shape `rule`,
   * `dataset` and `anomaly` already have. */
  contract: '/contracts/:contractId',
  reconciliation: '/reconciliation',
  /** One definition's runs, nested under the list so the breadcrumb back to it is a real parent —
   * the shape `dataset`, `rule`, `incident` and `anomaly` already have. Deep-linkable on purpose: a
   * reconciliation refusal is something somebody is *sent* ("why did rd-02 decline again"), and a
   * screen reachable only by clicking through a list has no address to send. */
  reconciliationDefinition: '/reconciliation/:definitionId',
  /** One run's masked breaks.
   *
   * **`/runs/:runId` rather than a bare second id**, for the reason `datasetsBySource` carries a
   * literal segment: `/reconciliation/:a/:b` is a URL whose two ids are distinguishable only by
   * which table they happen to exist in, which is the shape that produces a 404 nobody can explain. */
  reconciliationRun: '/reconciliation/:definitionId/runs/:runId',
  metering: '/usage',
  marketplace: '/plugins',
  /* `/copilot` rather than `/ai`: the screen is one capability of the AI plane, not the plane
   * itself, and a path named for the plane would have to move when a second AI screen lands. */
  copilot: '/copilot',
  /* `/suggestions` rather than `/rulegen`: a steward is looking for suggested rules, not for the
   * module that generated them — and the path a non-technical reader guesses is the noun, not the
   * subsystem. Under Quality in the nav for the same reason. */
  suggestions: '/suggestions',
  /* `/anomalies` rather than `/ai/anomaly`: a steward is looking for what looks wrong, not for
   * the detector. Under Quality beside incidents, because an actionable finding becomes one. */
  anomalies: '/anomalies',
  /* One finding, reached from the list's `Open`. Nested under it so the breadcrumb back to the
   * list is a real parent — the shape `rule`, `dataset` and `connection` already have. */
  anomaly: '/anomalies/:findingId',
  /* `/model-checks` rather than `/evals`: the reader is asking which models this deployment trusts
   * with which feature, and "evals" is the practitioner's word for the machinery that answers it.
   * Under Platform rather than Quality — it is a statement about the deployment, not about a
   * customer's data. */
  evals: '/model-checks',
  /* `/autonomous` rather than `/automation`: in shadow mode this screen is a decision LOG, and
   * naming it for the engine is what tells a reviewer whose decisions they are reading. */
  autonomous: '/autonomous',
  /* `/model-routing` rather than `/ai/serving`: the reader is asking where their metadata is sent,
   * and `serving` is the practitioner's word for the machinery that sends it. It pairs with
   * `/alert-routing` on purpose — both answer "where does this leave the platform, and to whom" —
   * and sits under Platform rather than Quality, because choosing an endpoint is a statement about
   * the deployment rather than a finding about a customer's data. */
  serving: '/model-routing',
  /* Was `/readiness`, which the dev proxy and a real EdgeGateway both claim for the operational
   * probe. The probe cannot move — a Kubernetes manifest points at it — so the screen did. */
  /* `/audit` rather than `/audit-chain` or `/compliance-log`: an auditor asks for "the audit trail",
   * and the noun a non-technical reader guesses is the right path. Under Platform in the nav rather
   * than Quality — it is a record of what was done to this deployment, not a finding about a
   * customer's data. */
  audit: '/audit',
  bulk: '/bulk-changes',
  policy: '/access-policy',
  platform: '/platform',
  /* ADR-012's local auth. `login`/`signup` are public, outside `AppShell` — see `App.tsx` for the
   * split. `team` is the ordinary, authenticated case: an admin already inside the app adding a
   * teammate. */
  login: '/login',
  signup: '/signup',
  /* The two halves of a forgotten password, and they are two routes rather than one with a mode.
   *
   * `/forgot-password` is reached by a person who cannot sign in; `/reset-password` is reached by a
   * link in their mail, carrying `?token=…`. Folding them into one path with a conditional would put
   * a token in the same address a reader types from memory, and the screen would have to decide which
   * of the two it was from the presence of a query parameter — which is exactly the shape that made
   * `/datasets/:id` and `/datasets/source/:id` two literal routes above.
   *
   * Both are public and neither redirects a signed-in reader away: a valid token in a tab does not
   * make a single-use reset token in an address bar less valid, and bouncing that reader to the
   * dashboard would strand the one thing that can spend it. */
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  /* Where a verification link lands. ADR-014, and a sibling of `/reset-password` rather than a mode
   * of it: both are reached by somebody holding a single-use token in a URL, and neither can be
   * reached any other way — but one proves an address and the other changes a password, and a
   * single route switching on a query parameter is how a reader ends up on the wrong form.
   *
   * The path is what `LUMIQ_VERIFICATION_LINK_TEMPLATE` must point at. It is deliberately spelled
   * here rather than derived from that setting: the frontend cannot read the backend's environment,
   * and a template pointing somewhere this router does not serve is a 404 an operator has to be
   * able to diagnose from one file. */
  verifyEmail: '/verify-email',
  /** Where an invited colleague lands from their activation mail.
   *
   * `/activate` rather than the brief's two addresses (`/activate` then `/set-password`), and the
   * reason is the token: a redirect between them would have to carry it, and the only two places
   * to put it are the URL — where it lands in browser history and in any proxy log — or client
   * state, which does not survive the reload a redirect invites. One address means the token is
   * read from the query string once and posted in a body twice.
   *
   * The two *steps* are still distinct and both visible: the link is validated on mount, and only
   * then is a password collected. */
  activate: '/activate',
  /* Where somebody whose password was chosen *for* them is sent, before anything else.
   *
   * **Authenticated but not usable.** Unlike the two above this is not public: the reader has a
   * valid token, and that token carries an obligation which makes every other route answer 403. So
   * it lives inside the signed-in half of the app and is the one destination `RequirePassword`
   * permits — a signed-in reader with the obligation is redirected here from anywhere, and a signed
   * -in reader without it is redirected away, because a screen asking for a password nobody needs
   * to change is a screen that invites one to be changed by accident. */
  changePassword: '/change-password',
  team: '/team',
  /* The tenant's identity and access surface: who can sign in, and what their role lets them do.
   * `team` predates it and is superseded - it is the add-user form alone - so it redirects here. */
  admin: '/admin',
  connectors: '/admin/connectors',
  /* Publishing the scoring model. Under `/admin` rather than on Scores, because a steward
   * reading yesterday's numbers is not the person changing what a number means — and a
   * published version alters the meaning of every score computed under it. */
  scoreModel: '/admin/score-model',
} as const;

export type ScreenPath = (typeof SCREEN_PATHS)[keyof typeof SCREEN_PATHS];

/** What one screen hands another when a reader arrives to act on something they were shown.
 *
 * **Here rather than in either feature slice, because both ends need it and neither owns it.** The
 * suggestions screen navigates with it and the rules screen reads it; putting the type in one of
 * them would make the other import a sibling feature, which is the dependency the slice layout
 * exists to avoid — and duplicating it would let the two spell the field differently, at which point
 * the prefill silently stops arriving with nothing failing.
 *
 * **An id, never the values.** Router state does not survive a reload or a shared link, so the
 * screen on the receiving end re-reads the fields from the API using this id. Carrying the fields
 * here would work until the first refresh and then present an empty form, which reads as a broken
 * feature rather than as expired state.
 */
export interface RulePrefill {
  /** The suggestion to prefill the rule form from, as `GET .../suggestions/{id}/rule-draft`. */
  readonly suggestionId: string;
}

/** Reads a `RulePrefill` out of an unknown router state. **Narrowed, never cast.**
 *
 * `useLocation().state` is `unknown` for a good reason — it is whatever the last navigation put
 * there, including nothing at all when a reader typed the URL — so a cast would hand a screen an
 * `undefined` id and turn a direct visit into a failed fetch for the empty string.
 */
export function rulePrefillFrom(state: unknown): RulePrefill | null {
  if (typeof state !== 'object' || state === null) return null;
  const id = (state as Record<string, unknown>)['suggestionId'];
  return typeof id === 'string' && id !== '' ? { suggestionId: id } : null;
}

/** Where `/` sends a visitor.
 *
 * **Now the dashboard, and it used to be the dataset catalogue** — with the reasoning "the catalog is
 * what the product is for". That was right while there was no better answer: landing somebody on a
 * deployment-health banner would have been worse. A dashboard that answers "is my data healthy, what
 * needs attention, what changed" is the better front door, and it is the question the product exists
 * to answer rather than one of its nouns. */
export const LANDING_PATH = SCREEN_PATHS.home;
